/**
 * PodcastsTab - Admin tab for managing podcast RSS feeds
 * Supports both Audio (RSS) and Video (YouTube) podcasts
 * 
 * Refactored: Extracted form and list item components to /podcasts/
 */
import { useState, useEffect } from 'react';
import { 
  Plus, Loader2, Headphones, Video, Youtube, RefreshCcw, X
} from 'lucide-react';
import { Button } from '../ui/button';
import { API, authHeaders } from './utils';
import {
  AudioPodcastForm,
  VideoPodcastForm,
  VideoEpisodeForm,
  AudioPodcastItem,
  VideoPodcastItem
} from './podcasts';

export const PodcastsTab = () => {
  const [activeTab, setActiveTab] = useState('audio');
  const [podcasts, setPodcasts] = useState([]);
  const [videoPodcasts, setVideoPodcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showVideoForm, setShowVideoForm] = useState(false);
  const [showEpisodeForm, setShowEpisodeForm] = useState(null);
  const [syncing, setSyncing] = useState({});
  
  const [formData, setFormData] = useState({
    rss_url: '', name: '', description: '', category: 'Sports'
  });
  
  const [videoFormData, setVideoFormData] = useState({
    youtube_url: '', name: '', description: '', category: 'Sports'
  });
  
  const [episodeFormData, setEpisodeFormData] = useState({
    youtube_url: '', title: '', description: '', thumbnail_url: ''
  });
  
  const [adding, setAdding] = useState(false);
  const [refreshing, setRefreshing] = useState({});
  const [error, setError] = useState('');

  useEffect(() => {
    fetchPodcasts();
    fetchVideoPodcasts();
  }, []);

  const fetchPodcasts = async () => {
    try {
      const res = await fetch(`${API}/podcasts/admin/list`, { headers: authHeaders() });
      if (res.ok) setPodcasts(await res.json() || []);
    } catch (err) {
      console.error('Failed to fetch podcasts:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchVideoPodcasts = async () => {
    try {
      const res = await fetch(`${API}/podcasts/admin/video/list`, { headers: authHeaders() });
      if (res.ok) setVideoPodcasts(await res.json() || []);
    } catch (err) {
      console.error('Failed to fetch video podcasts:', err);
    }
  };

  const handleAddPodcast = async (e) => {
    e.preventDefault();
    if (!formData.rss_url.trim()) { setError('RSS URL is required'); return; }
    setAdding(true);
    setError('');
    try {
      const res = await fetch(`${API}/podcasts/admin/add`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        await fetchPodcasts();
        setShowForm(false);
        setFormData({ rss_url: '', name: '', description: '', category: 'Sports' });
      } else {
        setError((await res.json()).detail || 'Failed to add podcast');
      }
    } catch { setError('Failed to add podcast'); }
    finally { setAdding(false); }
  };

  const handleAddVideoPodcast = async (e) => {
    e.preventDefault();
    if (!videoFormData.youtube_url.trim()) { setError('YouTube URL is required'); return; }
    setAdding(true);
    setError('');
    try {
      const res = await fetch(`${API}/podcasts/admin/video/add`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(videoFormData)
      });
      if (res.ok) {
        await fetchVideoPodcasts();
        setShowVideoForm(false);
        setVideoFormData({ youtube_url: '', name: '', description: '', category: 'Sports' });
      } else {
        setError((await res.json()).detail || 'Failed to add video podcast');
      }
    } catch { setError('Failed to add video podcast'); }
    finally { setAdding(false); }
  };

  const handleAddEpisode = async (e) => {
    e.preventDefault();
    if (!episodeFormData.youtube_url.trim() || !episodeFormData.title.trim()) {
      setError('YouTube URL and title are required'); return;
    }
    setAdding(true);
    setError('');
    try {
      const res = await fetch(`${API}/podcasts/admin/video/${showEpisodeForm}/add-episode`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(episodeFormData)
      });
      if (res.ok) {
        await fetchVideoPodcasts();
        setShowEpisodeForm(null);
        setEpisodeFormData({ youtube_url: '', title: '', description: '', thumbnail_url: '' });
      } else {
        setError((await res.json()).detail || 'Failed to add episode');
      }
    } catch { setError('Failed to add episode'); }
    finally { setAdding(false); }
  };

  const handleSyncVideoPodcast = async (podcastId) => {
    setSyncing(prev => ({ ...prev, [podcastId]: true }));
    try {
      const res = await fetch(`${API}/podcasts/admin/video/${podcastId}/sync`, {
        method: 'POST', headers: authHeaders()
      });
      const data = await res.json();
      if (res.ok) {
        await fetchVideoPodcasts();
        alert(data.new_episodes > 0 ? `Synced! Found ${data.new_episodes} new episode(s).` : 'No new episodes found.');
      } else setError(data.detail || 'Failed to sync podcast');
    } catch { setError('Failed to sync podcast'); }
    finally { setSyncing(prev => ({ ...prev, [podcastId]: false })); }
  };

  const handleSyncAllVideoPodcasts = async () => {
    setSyncing(prev => ({ ...prev, all: true }));
    try {
      const res = await fetch(`${API}/podcasts/admin/video/sync-all`, {
        method: 'POST', headers: authHeaders()
      });
      const data = await res.json();
      if (res.ok) {
        await fetchVideoPodcasts();
        alert(`Synced ${data.podcasts_synced} podcast(s). Found ${data.total_new_episodes} new episode(s) total.`);
      } else setError(data.detail || 'Failed to sync all podcasts');
    } catch { setError('Failed to sync all podcasts'); }
    finally { setSyncing(prev => ({ ...prev, all: false })); }
  };

  const handleRefresh = async (podcastId) => {
    setRefreshing(prev => ({ ...prev, [podcastId]: true }));
    try {
      const res = await fetch(`${API}/podcasts/admin/refresh/${podcastId}`, {
        method: 'POST', headers: authHeaders()
      });
      if (res.ok) await fetchPodcasts();
    } catch (err) { console.error('Failed to refresh:', err); }
    finally { setRefreshing(prev => ({ ...prev, [podcastId]: false })); }
  };

  const handleDeletePodcast = async (podcastId) => {
    if (!window.confirm('Delete this podcast?')) return;
    try {
      const res = await fetch(`${API}/podcasts/admin/${podcastId}`, {
        method: 'DELETE', headers: authHeaders()
      });
      if (res.ok) await fetchPodcasts();
    } catch (err) { console.error('Failed to delete:', err); }
  };

  const handleDeleteVideoPodcast = async (podcastId) => {
    if (!window.confirm('Delete this video podcast?')) return;
    try {
      const res = await fetch(`${API}/podcasts/admin/video/${podcastId}`, {
        method: 'DELETE', headers: authHeaders()
      });
      if (res.ok) await fetchVideoPodcasts();
    } catch (err) { console.error('Failed to delete:', err); }
  };

  const handleDeleteEpisode = async (podcastId, episodeId) => {
    if (!window.confirm('Delete this episode?')) return;
    try {
      const res = await fetch(`${API}/podcasts/admin/video/${podcastId}/episode/${episodeId}`, {
        method: 'DELETE', headers: authHeaders()
      });
      if (res.ok) await fetchVideoPodcasts();
    } catch (err) { console.error('Failed to delete:', err); }
  };

  const handleToggleActive = async (podcastId, currentStatus, isVideo = false) => {
    const endpoint = isVideo 
      ? `${API}/podcasts/admin/video/${podcastId}`
      : `${API}/podcasts/admin/${podcastId}`;
    try {
      const res = await fetch(endpoint, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !currentStatus })
      });
      if (res.ok) isVideo ? await fetchVideoPodcasts() : await fetchPodcasts();
    } catch (err) { console.error('Failed to toggle:', err); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="podcasts-admin-tab">
      {/* Tab Switcher */}
      <div className="flex items-center gap-4 border-b border-white/10 pb-4">
        <button
          onClick={() => setActiveTab('audio')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
            activeTab === 'audio' ? 'bg-orange-500 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'
          }`}
          data-testid="audio-podcasts-tab"
        >
          <Headphones className="w-4 h-4" />
          <span>Audio Podcasts</span>
          <span className="bg-black/20 px-2 py-0.5 rounded text-xs">{podcasts.length}</span>
        </button>
        <button
          onClick={() => setActiveTab('video')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
            activeTab === 'video' ? 'bg-orange-500 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'
          }`}
          data-testid="video-podcasts-tab"
        >
          <Video className="w-4 h-4" />
          <span>Video Podcasts</span>
          <span className="bg-black/20 px-2 py-0.5 rounded text-xs">{videoPodcasts.length}</span>
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/50 text-red-500 px-4 py-3 rounded-lg flex items-center gap-2">
          <X className="w-4 h-4" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Audio Podcasts Tab */}
      {activeTab === 'audio' && (
        <div className="space-y-4">
          {!showForm && (
            <Button onClick={() => setShowForm(true)} className="bg-orange-500 hover:bg-orange-600" data-testid="add-audio-podcast-btn">
              <Plus className="w-4 h-4 mr-2" />Add Audio Podcast (RSS)
            </Button>
          )}

          {showForm && (
            <AudioPodcastForm
              formData={formData}
              setFormData={setFormData}
              onSubmit={handleAddPodcast}
              onCancel={() => { setShowForm(false); setError(''); }}
              adding={adding}
            />
          )}

          <div className="space-y-3">
            {podcasts.length === 0 ? (
              <div className="text-center py-12 text-zinc-500">
                <Headphones className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No audio podcasts added yet</p>
                <p className="text-sm">Add a podcast by entering its RSS feed URL</p>
              </div>
            ) : (
              podcasts.map((podcast) => (
                <AudioPodcastItem
                  key={podcast.id}
                  podcast={podcast}
                  refreshing={refreshing[podcast.id]}
                  onRefresh={handleRefresh}
                  onToggleActive={handleToggleActive}
                  onDelete={handleDeletePodcast}
                />
              ))
            )}
          </div>
        </div>
      )}

      {/* Video Podcasts Tab */}
      {activeTab === 'video' && (
        <div className="space-y-4">
          {!showVideoForm && !showEpisodeForm && (
            <div className="flex gap-2">
              <Button onClick={() => setShowVideoForm(true)} className="bg-red-600 hover:bg-red-700" data-testid="add-video-podcast-btn">
                <Youtube className="w-4 h-4 mr-2" />Add Video Podcast
              </Button>
              {videoPodcasts.length > 0 && (
                <Button
                  onClick={handleSyncAllVideoPodcasts}
                  disabled={syncing.all}
                  variant="outline"
                  className="border-green-500/50 text-green-500 hover:bg-green-500/10"
                  data-testid="sync-all-btn"
                >
                  {syncing.all ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-2" />}
                  Sync All from YouTube
                </Button>
              )}
            </div>
          )}

          {showVideoForm && (
            <VideoPodcastForm
              formData={videoFormData}
              setFormData={setVideoFormData}
              onSubmit={handleAddVideoPodcast}
              onCancel={() => { setShowVideoForm(false); setError(''); }}
              adding={adding}
            />
          )}

          {showEpisodeForm && (
            <VideoEpisodeForm
              formData={episodeFormData}
              setFormData={setEpisodeFormData}
              onSubmit={handleAddEpisode}
              onCancel={() => { setShowEpisodeForm(null); setError(''); }}
              adding={adding}
            />
          )}

          <div className="space-y-4">
            {videoPodcasts.length === 0 ? (
              <div className="text-center py-12 text-zinc-500">
                <Video className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No video podcasts added yet</p>
                <p className="text-sm">Add a video podcast by entering a YouTube channel or playlist URL</p>
              </div>
            ) : (
              videoPodcasts.map((podcast) => (
                <VideoPodcastItem
                  key={podcast.id}
                  podcast={podcast}
                  syncing={syncing[podcast.id]}
                  onSync={handleSyncVideoPodcast}
                  onAddEpisode={setShowEpisodeForm}
                  onToggleActive={handleToggleActive}
                  onDelete={handleDeleteVideoPodcast}
                  onDeleteEpisode={handleDeleteEpisode}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PodcastsTab;
